"use client";

import HighlightTitle from "@/components/HighlightTitle";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { hasCtaTitle } from "@/lib/article-cta";
import { cn, tinaField } from "@/lib/utils";
import { ChevronRight } from "lucide-react";
import Link from "next/link";

const RELATED_TITLE = "Related posts";

export default function ArticleSidebar({ nextArticles = [], tinaFieldData, sidebarCta, className }) {
  const buttonLabel = sidebarCta?.button?.label;
  const buttonLink = sidebarCta?.button?.link || "/book-demo";
  const showSidebarCta = hasCtaTitle(sidebarCta?.title);
  const hasContent = showSidebarCta || nextArticles.length > 0;

  if (!hasContent) {
    return null;
  }

  return (
    <aside
      className={cn(
        "min-w-0 w-full",
        "max-lg:border-t max-lg:border-border max-lg:pt-8",
        "lg:sticky lg:top-24 lg:max-h-[calc(100dvh-6.5rem)] lg:self-start lg:overflow-y-auto lg:overscroll-contain",
        className
      )}
    >
      <div className="flex flex-col gap-5">
        {showSidebarCta && (
          <Card className="gap-0 overflow-visible border border-border bg-white p-0">
            <CardContent className="relative flex flex-col items-stretch gap-5 overflow-hidden rounded-xl p-4 sm:items-start sm:gap-6 sm:!p-6">
              <HighlightTitle
                as="h3"
                className="h3 text-balance !leading-relaxed text-title"
                content={sidebarCta.title}
                data-tina-field={tinaField(tinaFieldData, "sidebarCta.title")}
              />
              {buttonLabel && (
                <Button asChild arrow size="xs" className="w-full sm:w-auto" data-tina-field={tinaField(tinaFieldData, "sidebarCta.button.label")}>
                  <Link href={buttonLink}>{buttonLabel}</Link>
                </Button>
              )}
            </CardContent>
          </Card>
        )}

        {nextArticles.length > 0 && (
          <Card className="gap-0 overflow-visible border border-border bg-white p-0">
            <CardContent className="relative flex flex-col items-start gap-3 overflow-hidden rounded-xl p-4 sm:!p-5">
              <h3 className="h6 text-balance !leading-relaxed">{RELATED_TITLE}</h3>
              <ul className="relative z-10 w-full space-y-3">
                {nextArticles.map(({ slug, title }) => (
                  <li key={slug}>
                    <Link
                      href={`/blog/${slug}`}
                      className="group flex min-h-11 items-start gap-1.5 rounded-sm py-2 text-[15px] font-light leading-snug text-foreground underline-offset-4 hover:text-primary hover:underline sm:min-h-0 sm:py-0"
                    >
                      <ChevronRight className="mt-0.5 size-4 shrink-0 text-muted-foreground group-hover:text-primary sm:size-3.5" aria-hidden />
                      <span className="min-w-0 flex-1">{title}</span>
                    </Link>
                  </li>
                ))}
              </ul>
            </CardContent>
          </Card>
        )}
      </div>
    </aside>
  );
}
